import { App, Divider } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { get } from 'lodash-es';
import { useEffect } from 'react';

import { Text, Title } from '@atom/typography/typography';
import { Form } from '@form/common.style';
import { Label } from '@form/connector-auth/connector-auth.form-style';
import { FormError } from '@form/form-error';
import { Upload } from '@form/upload/upload';
import { useGetConnectionQuery, useSaveConnectionMetaMutation } from '@hook/connection/connection.hooks';
import { useStoreSelector } from '@hook/store/store.hooks';
import { ConnectorInterface } from '@interface/connector/connector.interface';
import { ErrorFormInterface } from '@interface/form/error-form.interface';
import { isExist, isString } from '@util/data/is-exist';

interface IntegrationSamlProps {
  connector: ConnectorInterface;
  connectionId?: string;
}

export const IntegrationSaml = ({ connector, connectionId }: IntegrationSamlProps) => {
  const { message } = App.useApp();
  const [form] = useForm();
  const connections = useStoreSelector((state) => state.connections.data);
  const connectionFromStore = connectionId ? connections[connectionId] : undefined;
  const { data, error } = useGetConnectionQuery(connector, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    skip: isExist(connectionFromStore),
  });
  const [saveMeta, result] = useSaveConnectionMetaMutation();

  const connectionData = connectionFromStore ?? data?.data;

  const acsUrl = get(connectionData, ['metadata', 'acsUrl']);
  const spEntityId = get(connectionData, ['metadata', 'spEntityId']);
  const ssoUrl = get(connectionData, ['settings', 'ssoUrl'], get(result, ['data', 'settings', 'ssoUrl']));
  const ipEntityId = get(connectionData, ['settings', 'ipEntityId'], get(result, ['data', 'settings', 'ipEntityId']));

  const handleUploadMeta = (file: any) => {
    saveMeta({
      serviceId: connector.serviceId,
      unifiedApi: connector.unifiedApi,
      file: file,
    });
  };

  useEffect(() => void (result.isSuccess && message.success('Identity provider updated')), [result.isSuccess]);

  if (!isString(acsUrl)) return null;

  return (
    <>
      <Title level={5} mb={16}>
        Service provider
      </Title>
      <Form form={form} labelCol={{ span: 2 }} wrapperCol={{ span: 20 }}>
        <Label label='ACS Url'>
          <Text level='s' weight={500}>
            {acsUrl}
          </Text>
        </Label>
        <Label label='SP Entity Id'>
          <Text level='s' weight={500}>
            {spEntityId}
          </Text>
        </Label>
      </Form>
      <Title level={5} mb={16} mt={16}>
        Identity provider
      </Title>
      <Form form={form} labelCol={{ span: 2 }} wrapperCol={{ span: 20 }}>
        {isString(ssoUrl) && (
          <Label label='ssoUrl' name={['settings', 'ssoUrl']}>
            <Text level='s'>{ssoUrl}</Text>
          </Label>
        )}
        {isString(ipEntityId) && (
          <Label label='ipEntityId' name={['settings', 'ipEntityId']}>
            <Text level='s'>{ipEntityId}</Text>
          </Label>
        )}
        {!result.isSuccess && <Upload text='Upload identity provider metadata' onFile={handleUploadMeta} />}
        <FormError error={error as ErrorFormInterface} errorField={[]} />
      </Form>
      <Divider />
    </>
  );
};
